import React, { useState, useEffect, useCallback } from "react";
import { Steps, Button, Icon, notification } from "antd";
import { useHistory, useLocation, Redirect } from "react-router-dom";
import firebase from "../../services/firebaseConfig";
import { isWemob } from "../../utils/is-wemob";
import api from "../../services/api";
import { getUserAdminData } from "../../services/adminData";
import { CompanyFormData, EMPTY_COMPANY, CUSTOMER_TYPE } from "./types";
import StepWelcome from "./StepWelcome";
import StepCompany from "./StepCompany";
import StepFinancial from "./StepFinancial";
import StepSuccess from "./StepSuccess";
import "./index.css";

const { Step } = Steps;

const LOGO_URL =
  "https://storage.googleapis.com/cpms-custom-themes-assets/tupi/logo_not_collapsed_1753135554.png";

const STEPS_CPO = [
  "Boas-vindas",
  "Dados da empresa",
  "Dados financeiros",
  "Concluído",
];
const STEPS_CONDO = ["Boas-vindas", "Dados da empresa", "Concluído"];

const Ativacao: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ registrationStatus?: string }>();

  const [step, setStep] = useState(0);
  const [company, setCompany] = useState<CompanyFormData>(EMPTY_COMPANY);
  const [submitting, setSubmitting] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<
    string | undefined
  >(location.state?.registrationStatus);
  const [checking, setChecking] = useState(registrationStatus === undefined);
  const isCPO = company.customerType === CUSTOMER_TYPE.CPO;
  const activeSteps = isCPO ? STEPS_CPO : STEPS_CONDO;

  const user = firebase.auth().currentUser;
  const userName = user?.displayName || user?.email?.split("@")[0] || "cliente";

  const handleLogout = async () => {
    await firebase.auth().signOut();
    history.push("/");
  };

  const goToSuccess = async () => {
    setSubmitting(true);
    try {
      await api.post("/company/onboarding", {
        isWemob,
        companyName: company.companyName,
        document: company.document,
        customerType: company.customerType,
        country: "BR",
        onboardingDocuments: company.onboardingDocuments
          .filter((d) => d.url)
          .map((d) => ({ url: d.url, description: d.description })),
        ...(isCPO && {
          financial: {
            bank: company.financial.bank || null,
            agency: company.financial.agency || null,
            account: company.financial.account || null,
            pixKey: company.financial.pixKey || null,
            emails: company.financial.emails.filter((e) => e.trim()),
          },
        }),
      });
      setStep(isCPO ? 3 : 2);
    } catch (error: any) {
      notification.error({
        message: "Erro ao enviar cadastro",
        description: error?.response?.data?.showError
          ? error.response.data.message
          : "Não foi possível salvar os dados da empresa. Tente novamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleActiveStatus = useCallback(async () => {
    try {
      const tokenInfo = await firebase.auth().currentUser?.getIdTokenResult(true);
      const adminData = await getUserAdminData(tokenInfo?.claims?.email);
      if (tokenInfo && adminData?.is_admin) {
        history.push({ pathname: "/dashboard", state: { from: "login" } });
      } else {
        setRegistrationStatus("active");
      }
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 403 || status === 404) {
        setRegistrationStatus("active");
        return;
      }

      console.error("Failed to check admin status:", error);
      notification.error({
        message: "Erro ao verificar status",
        description:
          "Não foi possível verificar seus dados de administrador. Tente novamente.",
      });
      setRegistrationStatus("active");
    }
  }, [history]);

  useEffect(() => {
    if (registrationStatus === "active") {
      handleActiveStatus();
      return;
    }
    if (registrationStatus !== undefined) return;
    api
      .get("/company/registration-status")
      .then(async ({ data: { registrationStatus: fetched } }) => {
        if (fetched === "active") {
          await handleActiveStatus();
        } else {
          setRegistrationStatus(fetched);
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [handleActiveStatus, registrationStatus]);

  const isSuccess =
    registrationStatus === "draft" ||
    registrationStatus === "active" ||
    (isCPO ? step === 3 : step === 2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  if (checking) {
    return (
      <div
        className="ativacao-root"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Icon type="loading" style={{ fontSize: 32, color: "#5b00ef" }} />
      </div>
    );
  }

  return (
    <div className="ativacao-root">
      <header className="ativacao-topbar">
        <img src={LOGO_URL} alt="Tupi" className="ativacao-topbar-logo" />
        <div className="ativacao-topbar-right">
          <span className="ativacao-topbar-user">{userName}</span>
          <Button type="link" size="small" onClick={handleLogout} title="Sair">
            <Icon type="logout" />
          </Button>
        </div>
      </header>

      <main className="ativacao-main">
        <div className="ativacao-card">
          {!isSuccess && (
            <>
              <div className="ativacao-stepper-wrap">
                <Steps current={step} size="small">
                  {activeSteps.map((label) => (
                    <Step key={label} title={label} />
                  ))}
                </Steps>
              </div>
              <div className="ativacao-progress-mobile">
                <div className="ativacao-progress-header">
                  <span className="ativacao-progress-title">
                    {activeSteps[step]}
                  </span>
                  <span className="ativacao-progress-label">
                    {step + 1}/{activeSteps.length}
                  </span>
                </div>
                <div className="ativacao-progress-bar">
                  <div
                    className="ativacao-progress-fill"
                    style={{
                      width: `${((step + 1) / activeSteps.length) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {!isSuccess && step === 0 && (
            <StepWelcome userName={userName} onStart={() => setStep(1)} />
          )}
          {!isSuccess && step === 1 && (
            <StepCompany
              value={company}
              onChange={setCompany}
              onBack={() => setStep(0)}
              onNext={() => (isCPO ? setStep(2) : goToSuccess())}
              submitting={!isCPO && submitting}
            />
          )}
          {!isSuccess && step === 2 && isCPO && (
            <StepFinancial
              value={company}
              onChange={setCompany}
              onBack={() => setStep(1)}
              onNext={goToSuccess}
              submitting={submitting}
            />
          )}
          {isSuccess && (
            <StepSuccess
              userName={userName}
              companyName={company.companyName}
              userEmail={user?.email || ""}
              onLogout={handleLogout}
            />
          )}
        </div>
      </main>
    </div>
  );
};

const AtivacaoGuard: React.FC = () => {
  if (!isWemob) {
    return <Redirect to="/dashboard" />;
  }
  return <Ativacao />;
};

export default AtivacaoGuard;
